import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/shadcn/raw/dropdown-menu';
import { ScrollArea } from '@/shared/shadcn/raw/scroll-area';
import { cn } from '@/shared/shadcn/lib/utils';
import styled from 'styled-components';

type Option<T> = {
  label: string | React.ReactNode;
  value: T;
};

interface DropdownProps<T> {
  options: Option<T>[];
  onSelect: (option: Option<T>) => void;
  selectedValue?: T;
  optionRender?: (option: Option<T>, index: number) => React.ReactNode;
  className?: string;
  isFullWidth?: boolean; // 드롭다운 컨텐츠 영역 너비 조정
  dropdownContentClassName?: string; // 드롭다운 컨텐츠 영역 클래스
  optionClassName?: string; // 드롭다운 개별 옵션 클래스
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
}

export default function CustomDropdown<T>({
  isFullWidth = false,
  options,
  onSelect,
  selectedValue,
  optionRender,
  onOpenChange,
  children,
  className = '',
  dropdownContentClassName = '',
  optionClassName = '',
  sideOffset = 10,
  alignOffset = 0,
  align = 'center',
}: DropdownProps<T>) {
  return (
    <DropdownMenu onOpenChange={(open) => onOpenChange?.(open)}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <S.DropdownMenuContent
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={`shadow-[0px_8px_8px_0px_rgba(0,0,0,0.16)] ${isFullWidth && 'full-width'} ${className}`}>
        <ScrollArea>
          <div className={cn('max-h-60 p-1', dropdownContentClassName)}>
            {options && options?.length > 0 ? (
              options.map((option, index) => (
                <DropdownMenuItem
                  key={`dropdown-${index}-${option.value}`}
                  className={cn(
                    'my-0.5 h-auto min-h-9 cursor-pointer items-start rounded-xl px-3 py-2 text-[0.875rem] leading-[1.4] wrap-break-word whitespace-normal text-gray-900',
                    selectedValue === option.value && 'bg-muted option_selected',
                    option.value === '__none__' && 'pointer-events-none',
                    option.value === '__disabled__' && 'custom-dropdown-item-disabled pointer-events-none',
                    optionClassName
                  )}
                  onSelect={() => onSelect(option)}>
                  <span className='min-w-0 flex-1'>
                    {optionRender ? optionRender(option, index) : typeof option.label === 'string' ? option.label : option.label}
                  </span>
                </DropdownMenuItem>
              ))
            ) : (
              <div className='flex h-20 w-full items-center justify-center'>No data</div>
            )}
          </div>
        </ScrollArea>
      </S.DropdownMenuContent>
    </DropdownMenu>
  );
}

const S = {
  DropdownMenuContent: styled(DropdownMenuContent)`
    border: none;
    padding: 0;
    border-radius: 0.75rem;
    box-shadow: 8px 8px 30px 0 rgba(0, 13, 59, 0.16);
    // 트리거 너비만큼 너비 조정 ( 공식 문서: https://www.radix-ui.com/primitives/docs/components/dropdown-menu#constrain-the-contentsub-content-size )
    &.full-width {
      min-width: 100%;
      width: var(--radix-dropdown-menu-trigger-width);
    }
  `,
};
